/**
 * MachinesList — Tab de Máquinas (tabela estilo painel admin, igual a AgentsList)
 * Colunas: Status | Nome | Agentes | CPU | RAM | HD | Plano | Ações
 */
import { Button, Icon, Spinner } from '../../components/ui';
import { T } from '../../styles/tokens';
import { MachineRow } from './MachineCard';
import type { Environment } from './types';

interface MachinesListProps {
  environments: Environment[];
  userPlan?: string;
  loading?: boolean;
  justCreated?: boolean;
  restartingIds: Set<string>;
  onCreateEnv: () => void;
  onRestart: (env: Environment) => void;
  onRefresh: () => void;
  onLogs: (env: Environment) => void;
  onDelete: (env: Environment) => void;
}

function ColHeader({ children, flex, align, width }: { children: string; flex?: number; align?: string; width?: number }) {
  return (
    <div style={{
      flex: flex ?? undefined, width: width ?? undefined, minWidth: 0, paddingRight: 12,
      fontSize: 10, fontWeight: 700, color: T.textSecondary,
      textTransform: 'uppercase' as const, letterSpacing: '0.8px',
      textAlign: (align as any) || 'left', flexShrink: width ? 0 : undefined,
    }}>
      {children}
    </div>
  );
}

export function MachinesList({ environments, userPlan, loading, justCreated, restartingIds, onCreateEnv, onRestart, onRefresh, onLogs, onDelete }: MachinesListProps) {
  if (loading) {
    return (
      <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
            borderBottom: `1px solid ${T.border}`,
          }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: T.bgHover, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ width: '35%', height: 12, background: T.bgHover, borderRadius: 4, marginBottom: 6 }} />
              <div style={{ width: '55%', height: 10, background: T.bgHover, borderRadius: 4 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (environments.length === 0) {
    if (justCreated) {
      return (
        <div style={{
          background: T.bgCard, borderRadius: 14, border: `1px solid ${T.border}`,
          padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, minHeight: 180,
        }}>
          <Spinner size="md" inline />
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Criando ambiente...</div>
          <div style={{ fontSize: 12, color: T.textSecondary }}>Isso pode levar alguns segundos</div>
        </div>
      );
    }
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '80px 24px', gap: 12,
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Nenhuma máquina</div>
        <div style={{ fontSize: 13, color: T.textSecondary, textAlign: 'center', maxWidth: 360, marginBottom: 16 }}>
          Crie seu primeiro ambiente para hospedar agentes de IA
        </div>
        <Button variant="ghost" size="sm" onClick={onCreateEnv} icon={<Icon name="plus" size={14} />} style={{ borderRadius: 20 }}>
          Criar Ambiente
        </Button>
      </div>
    );
  }

  return (
    <div style={{
      borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden',
      background: T.bgCard, marginBottom: 32,
    }}>
      {/* Header da tabela — desktop only */}
      <div class="machine-header-desktop" style={{
        display: 'flex', alignItems: 'center', gap: 0,
        padding: '10px 16px',
        borderBottom: `1px solid ${T.border}`,
        background: T.bgSubtle || T.bgInput,
      }}>
        <div style={{ width: 26, flexShrink: 0 }} /> {/* Status dot spacer */}
        <ColHeader flex={2}>Máquina</ColHeader>
        <ColHeader flex={1.5}>Agentes</ColHeader>
        <ColHeader flex={0.7} align="right">CPU</ColHeader>
        <ColHeader flex={0.7} align="right">RAM</ColHeader>
        <ColHeader flex={0.7} align="right">HD</ColHeader>
        <ColHeader flex={0.8}>Plano</ColHeader>
        <ColHeader flex={0.8}>Status</ColHeader>
        <div style={{ width: 110, flexShrink: 0 }} /> {/* Actions spacer */}
      </div>

      {/* Rows */}
      {environments.map(env => (
        <MachineRow
          key={env.id}
          env={env}
          userPlan={userPlan}
          isRestarting={restartingIds.has(env.id || '')}
          onRestart={() => onRestart(env)}
          onRefresh={onRefresh}
          onLogs={() => onLogs(env)}
          onDelete={() => onDelete(env)}
        />
      ))}
    </div>
  );
}
